// CabinOutside
// Features:
// - Renders exterior cabin mesh with custom shader (lights enabled)
// - Renders windows with alpha mapping and light-dependent color
// - Includes physics bounds for the exterior

import { useMemo } from 'react'
import { useTexture } from '@react-three/drei'
import { RigidBody } from '@react-three/rapier'
import * as THREE from 'three'

import { useStore } from '../../store/useStore'
import { getPointLightUniforms } from '../../utils/lighting'

import vertexShaderOutside from '../shaders/cabin/outside/vertex.glsl'
import fragmentShaderOutside from '../shaders/cabin/outside/fragment.glsl'
import windowsVertex from '../shaders/windows/vertex.glsl'
import windowsFragment from '../shaders/windows/fragment.glsl'

import windowsTextureURL from '/textures/windows_alpha.jpg'

const LIGHT_CONFIG = {
    // Point Light 1
    p1PosX: 3.27,
    p1PosY: 1.69,
    p1PosZ: -1.93,
    p1Color: '#e6bf71',
    p1Intensity: 2.0,
    p1Decay: 0.5,

    // Point Light 2
    p2PosX: -0.22,
    p2PosY: 2.12,
    p2PosZ: 5.0,
    p2Color: '#e6bf71',
    p2Intensity: 2.0,
    p2Decay: 0.5,

    // Point Light 3
    p3PosX: 3.84,
    p3PosY: 3.39,
    p3PosZ: 0.64,
    p3Color: '#e6bf71',
    p3Intensity: 1.32,
    p3Decay: 0.25,

    // Common
    pSpecularPower: 1.0,
    ambientIntensity: 0.5,
}

const WINDOWS_CONFIG = {
    color: '#ffffff',
    opacity: 0.5,
}

export default function CabinOutside({ nodes, cabinTexture, physicsMaterial }) {
    const lightsOn = useStore((state) => state.lightsOn)

    // Textures
    const windowsAlphaMap = useTexture(windowsTextureURL, (texture) => {
        texture.flipY = false
    })

    // Materials
    const windowsMaterial = useMemo(() => {
        const col = lightsOn ? WINDOWS_CONFIG.color : '#777777'

        return new THREE.ShaderMaterial({
            uniforms: {
                uMap: { value: windowsAlphaMap },
                uColor: { value: new THREE.Color(col) },
                uOpacity: { value: WINDOWS_CONFIG.opacity },
            },
            vertexShader: windowsVertex,
            fragmentShader: windowsFragment,
            transparent: true,
            depthWrite: false,
            side: THREE.DoubleSide,
        })
    }, [windowsAlphaMap, lightsOn])

    const materialOutside = useMemo(() => {
        const pointFactor = lightsOn ? 1.0 : 0.1
        const ambientFactor = lightsOn ? 1.0 : 0.2

        return new THREE.ShaderMaterial({
            uniforms: {
                uColor: new THREE.Uniform(new THREE.Color('#ff0000')),
                uMap: { value: cabinTexture },
                uIntensity: {
                    value: LIGHT_CONFIG.ambientIntensity * ambientFactor,
                },
                ...getPointLightUniforms(LIGHT_CONFIG, pointFactor),
            },
            vertexShader: vertexShaderOutside,
            fragmentShader: fragmentShaderOutside,
        })
    }, [cabinTexture, lightsOn])

    return (
        <group>
            {/* Exterior Mesh */}
            <mesh
                geometry={nodes.cabin_out.geometry}
                castShadow
                receiveShadow
                material={materialOutside}
            />

            {/* Windows */}
            <mesh
                geometry={nodes.windows_glass.geometry}
                castShadow
                receiveShadow
                material={windowsMaterial}
            />

            {/* Physics Collider */}
            <RigidBody
                colliders="trimesh"
                type="fixed"
                userData={{ name: 'cabin_outside' }}
            >
                <mesh
                    geometry={nodes.cabin_out_physics.geometry}
                    material={physicsMaterial}
                />
            </RigidBody>
        </group>
    )
}
